import { BlockData, BlockModel, BlockRemoteData } from "@courses-models/course/src/ts/models/BlockModel";
import { makeObservable, observable } from "mobx";

/**
 * Enumeration of all supported product types, from which we can reference
 * content.
 */
export enum ProductTypes {
    PM2 = "PM2",
}

/**
 * Enumeration of all supported element types, which can be loaded and rendered
 * in the course-viewer.
 */
export enum ElementTypes {
    ACCORDION = "Accordion",
    AUDIO = "Audio",
    COMPLEXT_CONTENT = "Complexcontent",
    IFRAME = "Iframe",
    IMAGE = "Image",
    RICHTEXT = "Richtext",
    SLIDER = "Slider",
    VIDEO = "Video",
    PAGE = "Page",
    IBOOK = "Ibook",
}

/**
 * Specification of raw data required to create a new ReferenceBlockModel.
 */
type ReferenceBlockData = BlockData & {
    element: {
        id: string;
        title: string;
        type: ElementTypes; // tslint:disable-line no-reserved-keywords
    };
    product: {
        id: string;
        title: string;
        type: ProductTypes; // tslint:disable-line no-reserved-keywords
    };
};

/**
 * Specification of the format used when serializing reference block content for
 * storage in the remote backend.
 */
type ReferenceBlockRemoteData = {
    element: {
        id: string;
        title: string;
        type: ElementTypes; // tslint:disable-line no-reserved-keywords
    };
    product: {
        id: string;
        title: string;
        type: ProductTypes; // tslint:disable-line no-reserved-keywords
    };
};

/**
 * Reference blocks point to content that should be fetched from inside some of
 * Gyldendal's own products..
 */
export class ReferenceBlockModel extends BlockModel {
    /**
     * Specification of the element, which the block is pointing to (so we can
     * fetch it's data later on).
     */
    public readonly element: {
        /**
         * Contains the id of the element, as defined in the remote backend.
         */
        readonly id: string;

        /**
         * Contains the title of the element, as defined in the remote backend.
         */
        readonly title: string;

        /**
         * Specifies the type of the element (ie. what kind of content it's
         * referring to).
         */
        readonly type: ElementTypes; // tslint:disable-line no-reserved-keywords
    };

    /**
     * Specification of the product, which the element in this block was fetched
     * from (so we can fetch it's data later on).
     */
    public readonly product: {
        /**
         * Contains the id of the product, as defined in the remote backend.
         */
        readonly id: string;

        /**
         * Contains the id of the product, as defined in the remote backend.
         */
        readonly title: string;

        /**
         * Specifies the type of the product (so we know how to fetch the
         * element from the correct backend).
         */
        readonly type: ProductTypes; // tslint:disable-line no-reserved-keywords
    };

    /**
     * Apply initial data when the model is instantiated.
     */
    constructor(initial: ReferenceBlockData) {
        super(initial);

        this.element = initial.element;
        this.product = initial.product;

        makeObservable(this, {
            title: observable,
            note: observable,
        });
    }

    /**
     * Helper that'll create an instance of the LinkBlockModel based on a
     * serialized data from the remote backend.
     */
    public static DESERIALIZE(data: BlockRemoteData): ReferenceBlockModel {
        const content = <ReferenceBlockRemoteData>JSON.parse(data.content);

        return new ReferenceBlockModel({
            id: data.id,
            note: {
                content: data.note.content,
                enabled: data.note.isVisible,
            },
            title: data.title,
            ...content,
        });
    }

    /**
     * Helper that'll return a serialized version of the content, so that it can
     * be stored remotely.
     */
    public serializeContent(): string {
        return JSON.stringify({
            element: this.element,
            product: this.product,
        });
    }

    /**
     * Helper that'll generate a snapshot of the content within this block.
     */
    protected getContentSnapshot(): string {
        return JSON.stringify({
            elementId: this.element.id,
            elementType: this.element.type,
            productId: this.product.id,
            productType: this.product.type,
        });
    }
}
